/**
 * TeamCrest — circular team-badge image with initials fallback.
 *
 * Renamed from `atoms/TeamBadge` as part of Phase 0b. Behaviour is
 * unchanged: when `src` is missing / empty / 404s, the component
 * collapses to a two-letter initial badge that reads the team name.
 * The initials fallback is the universal default — engines ship the
 * URL when one exists, and the render layer never invokes a tenant-
 * specific badge resolver.
 *
 * Shape: { name: string; src?: string; size?: number }
 */

import { useState, type ReactElement } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import { useTenantConfig } from '../../tenant/useTenantConfig.js';
import { lookupTeamColors } from '../atoms/team-colors-data.js';
import styles from './TeamCrest.module.css';
import { inkOn } from '../../lib/inkOn.js';

/**
 * Brand team-visual mode (ADR-063 addendum, 2026-07-31). Official club
 * crests are a licensing RARITY — the market standard is that betting
 * brands may NOT show official logos or kits. The `--team-visual` brand
 * dial picks the presentation:
 *   'crest'  — official badge images (licensed brands, e.g. the original
 *              sponsorship-activation tenant). Platform default.
 *   'colors' — team-colour disc with initials (kit colours via the
 *              team-colours lookup; neutral disc when unknown).
 *   'none'   — no visual next to team names at all.
 * Read from the served brand tokens, so the CMS/Design Studio controls
 * it per brand — no component branches on tenant identity.
 */
export type TeamVisualMode = 'crest' | 'colors' | 'none';

export function useTeamVisualMode(): TeamVisualMode {
  const tenant = useTenantConfig();
  if (tenant.kind !== 'live' && tenant.kind !== 'paused') return 'crest';
  const v = tenant.config.brand.tokens['--team-visual'];
  return v === 'colors' || v === 'none' ? v : 'crest';
}

/**
 * Extract the Sportradar competitor URN embedded in engine badge URLs
 * (`/onsite/assets/teams/sr%3Acompetitor%3A1295/badge?v=…`) so colour
 * mode can resolve kit colours without threading a urn prop through
 * every composition.
 */
function urnFromSrc(src: string | undefined): string | null {
  if (src === undefined) return null;
  const m = /sr(?::|%3A)competitor(?::|%3A)(\d+)/i.exec(src);
  return m?.[1] !== undefined ? `sr:competitor:${m[1]}` : null;
}


const dataShape = z.object({
  /** Team name — used for alt text and the initials fallback. */
  name: z.string(),
  /** URL to the badge image. When falsy / errors, renders initials. */
  src: z.string().url().optional(),
  /** Render diameter in px. Defaults to 24. */
  size: z.number().int().positive().optional(),
});

export const META: PrimitiveMeta = {
  name: 'team-crest',
  description: 'Circular team badge with initials fallback.',
  composable: true,
  dataShape,
  densities: ['compact', 'standard'],
  breakpoints: { mobile: true, wide: true },
  animation: 'none',
};

interface TeamCrestProps {
  /** URL to the badge image. When falsy, renders the initials fallback. */
  src?: string;
  /** Team name — used for alt text and the initials fallback. */
  name: string;
  /** Render size in px. Defaults to 24. */
  size?: number;
}

/**
 * Build initials from a team name: take the first letter of each word, up
 * to 2 letters. Empty input → '?'. Brand-neutral by design — engine ships
 * the badge URL when one exists; the fallback never assumes a particular
 * league or club mark.
 */
function initialsFromName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const letters = words
    .map((w) => w.charAt(0).toUpperCase())
    .filter((c) => c !== '')
    .slice(0, 2)
    .join('');
  return letters || '?';
}

const TeamCrest = ({ src, name, size = 24 }: TeamCrestProps): ReactElement | null => {
  const [failed, setFailed] = useState(false);
  const mode = useTeamVisualMode();

  const containerStyle = {
    width: `${size}px`,
    height: `${size}px`,
  };

  if (mode === 'none') return null;

  if (mode === 'colors') {
    const urn = urnFromSrc(src);
    const triple =
      (urn !== null ? lookupTeamColors(urn) : null) ?? lookupTeamColors(name);
    const bg = triple?.[0];
    return (
      <div
        className={styles.containerEmpty}
        style={{
          ...containerStyle,
          ...(bg !== undefined ? { background: bg, color: inkOn(bg) } : {}),
        }}
        aria-label={name}
        role="img"
      >
        {initialsFromName(name)}
      </div>
    );
  }

  if (src && src.trim() !== '' && !failed) {
    return (
      <div className={styles.container} style={containerStyle}>
        <img
          src={src}
          alt={name}
          className={styles.image}
          loading="lazy"
          decoding="async"
          onError={(): void => setFailed(true)}
        />
      </div>
    );
  }

  return (
    <div
      className={styles.containerEmpty}
      style={containerStyle}
      aria-label={name}
      role="img"
    >
      {initialsFromName(name)}
    </div>
  );
};

export { TeamCrest };
export default TeamCrest;
